import { chatClient } from "@/api/grpc";
import { auth } from "@/api/firebase";
import { makeThreadPath } from "@/lib/proto";
import { create } from "@bufbuild/protobuf";
import { 
  MessageSchema, 
  ChunkType, 
  TextContentSchema,
  CompositionStatus,
  Role
} from "@generated/proto/model";
import type { 
  StreamMessageChunk
} from "@generated/proto/model";
import { 
  SendStreamingMessageRequestSchema
} from "@generated/proto/service";
import { useCallback, useState } from "react";

export interface StreamingMessage {
  messageId: string;
  content: string;
  isComplete: boolean;
}

export interface StreamingChunkCallbacks {
  onUserMessageCreated?: (messageId: string) => void;
  onPlaceholderCreated?: (messageId: string) => void;
  onAgentTextChunk?: (messageId: string, content: string) => void;
  onAgentComplete?: (messageId: string) => void;
  onError?: (error: string) => void;
}

export function useSendStreamingMessage() {
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize sendMessage to ensure stable reference across renders (prevents unnecessary useEffect re-runs)
  // Empty dependency array is correct: setState functions are guaranteed stable by React
  const sendMessage = useCallback(async (
    tripId: string,
    threadId: string,
    content: string,
    callbacks?: StreamingChunkCallbacks
  ): Promise<void> => {
    if (!tripId?.trim() || !threadId?.trim()) {
      throw new Error("tripId and threadId are required");
    }

    if (!content?.trim()) {
      throw new Error("message content is required");
    }

    const user = auth.currentUser;
    if (!user) {
      const err = new Error("User not authenticated");
      setError(err.message);
      throw err;
    }

    setIsStreaming(true);
    setError(null);

    try {
      // Create the message request
      const message = create(MessageSchema, {
        threadId,
        content: {
          case: "textContent",
          value: create(TextContentSchema, {
            content,
            compositionStatus: CompositionStatus.COMPLETE,
          })
        },
        role: Role.USER,
      });

      const request = create(SendStreamingMessageRequestSchema, {
        threadPath: makeThreadPath(user.uid, tripId, threadId),
        message,
      });

      // Start streaming
      const stream = chatClient.sendStreamingMessage(request);

      for await (const response of stream) {
        if (response.chunk) {
          const chunk: StreamMessageChunk = response.chunk;
          
          switch (chunk.chunkType) {
            case ChunkType.USER_MESSAGE_CREATED:
              callbacks?.onUserMessageCreated?.(chunk.messageId);
              break;
              
            case ChunkType.AGENT_PLACEHOLDER_CREATED:
              callbacks?.onPlaceholderCreated?.(chunk.messageId);
              break;
              
            case ChunkType.AGENT_TEXT:
              callbacks?.onAgentTextChunk?.(chunk.messageId, chunk.content);
              break;
              
            case ChunkType.AGENT_COMPLETE:
              callbacks?.onAgentComplete?.(chunk.messageId);
              break;
              
            case ChunkType.ERROR:
              const errorMsg = chunk.content || "Unknown streaming error";
              setError(errorMsg);
              callbacks?.onError?.(errorMsg);
              break;
              
            default:
              console.warn("Unknown chunk type:", chunk.chunkType);
              break;
          }
        }

        // Handle final message if provided
        if (response.message) {
          // The final message is typically handled via repository/Firestore updates
        }
      }
    } catch (err: any) {
      console.error("[useSendStreamingMessage] error", err);
      const errorMessage = err?.message || "Failed to send streaming message";
      setError(errorMessage);
      callbacks?.onError?.(errorMessage);
      throw err;
    } finally {
      setIsStreaming(false);
    }
  }, []);

  return {
    sendMessage,
    isStreaming,
    error,
  };
}